import React from 'react';
import {
    getPaymentMethods,
    getStancerAddCard,
    getStancerOneOffHpp,
    validateStancerAddCardHpp,
    validateStancerOneOffHpp,
    validateStancerOneOffHppExistingCard,
} from './stancer.api';
import { publish } from 'pubsub-js';
import {
    MasterCardIcon,
    VISAIcon,
} from '../../../sharedcomponents/inputs/svg-icons/CustomSvgIcons';
import { PAYMENT_METHOD_TYPE } from '../../../components/myeirconstant/myEirConstant';
import moment from 'moment';

const { CREDIT_CARD } = PAYMENT_METHOD_TYPE;
/**
 * @name getStancerSelfcareAddcardHpp - to get add card iframe
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {boolean} Obj.setOpenErrorPopup - Sets the error state when API is throwing error
 * @param {boolean} Obj.setLoading - Sets the loading state
 * @returns {Promise} Resolves in to payment gateway provider
 */
export const getStancerSelfcareAddcardHpp = async ({ graphql, setOpenErrorPopup, setLoading }) => {
    try {
        const data = await getStancerAddCard({ graphql });
        return data;
    } catch (error) {
        setOpenErrorPopup(true);
        setLoading(false);
        console.error(error);
    }
};

/**
 * @name validateStancerAddCardSelfCareHpp Validate the added card details
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} paymentId - paymentIntentId from getStancerSelfcareAddcardHpp response
 * @param {boolean} setOpenErrorPopup - Sets the error state when API is throwing error
 * @returns {string} Promise and resolves to add card response
 */
const validateStancerAddCardSelfCareHpp = async ({ graphql, paymentId, setOpenErrorPopup }) => {
    try {
        const data = await validateStancerAddCardHpp({
            graphql,
            variables: {
                token: sessionStorage.getItem('access_token'),
                paymentIntentId: paymentId,
            },
        });
        return data;
    } catch (error) {
        setOpenErrorPopup(true);
        return error;
    }
};

/**
 * @name getStancerOneOffSelfcareHpp - to get one off payment iframe
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {string} Obj.payableAmount - Amount to be paid
 * @param {boolean} Obj.setOpenErrorPopup - Sets the error state when API is throwing error
 * @param {boolean} Obj.setLoading - Sets the loading state
 * @returns {Promise} Resolves in to payment gateway provider
 */
export const getStancerOneOffSelfcareHpp = async ({
    graphql,
    payableAmount,
    setOpenErrorPopup,
    setLoading,
}) => {
    try {
        const data = await getStancerOneOffHpp({
            graphql,
            variables: {
                token: sessionStorage.getItem('access_token'),
                paymentRequest: { amount: Math.round(payableAmount * 100) },
            },
        });
        return data;
    } catch (error) {
        setOpenErrorPopup(true);
        setLoading(false);
        console.error(error);
    }
};

/**
 * @name validateStancerOneOffSelfCareHpp Validate the one off card details
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} paymentId - paymentIntentId from getStancerSelfcareAddcardHpp response
 * @param {object} checked - Checkbox status
 * @param {boolean} setOpenErrorPopup - Sets the error state when API is throwing error
 * @param {boolean} setPaymentErrorPopup - Sets the state of payment error
 * @returns {string} Promise and resolves to add card response
 */
export const validateStancerOneOffSelfCareHpp = async ({
    graphql,
    paymentId,
    checked,
    setPaymentErrorPopup,
    setOpenErrorPopup,
}) => {
    try {
        const data = await validateStancerOneOffHpp({
            graphql,
            variables: {
                token: sessionStorage.getItem('access_token'),
                hppResponse: {
                    paymentIntentId: paymentId,
                    saveCard: checked,
                },
            },
        });
        return data;
    } catch (error) {
        setPaymentErrorPopup(true);
        setOpenErrorPopup(true);
        return error;
    }
};

/**
 * @name getAddCardIframe Get the add card iframe
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} setHpp - Sets the hpp reponse from add card
 * @param {boolean} setLoading-sets the state of the loading iframe
 * @param {boolean} setOpenErrorPopup - Sets the error state when API is throwing error
 * @returns {string} Promise and resolves to add card response
 */
export const getAddCardIframe = async ({ graphql, setHpp, setLoading, setOpenErrorPopup }) => {
    try {
        const response = await getStancerSelfcareAddcardHpp({
            graphql,
            setOpenErrorPopup,
            setLoading,
        });
        if (response) {
            setHpp(response?.getStancerAddCardSelfCareHpp);
            setLoading(false);
        }
    } catch (error) {
        return error;
    }
};

/**
 * @name validateAddCard Validate the added card details
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} paymentId - PaymentIntentId from add card response
 * @param {boolean} setCardAdded - Specifies state of adding card
 * @param {boolean} toggle - Specifies state of success modal
 * @param {boolean} setOpenErrorPopup - Sets the error state when API is throwing error
 * @returns {string} Promise and resolves to add card response
 */
export const validateAddCard = async ({
    graphql,
    paymentId,
    setCardAdded,
    toggle,
    setOpenErrorPopup,
}) => {
    try {
        const response = await validateStancerAddCardSelfCareHpp({
            graphql,
            paymentId,
            setOpenErrorPopup,
        });
        if (response?.validateStancerAddCardSelfCareHpp === 'success') {
            publish('UPDATE_CARD');
            setCardAdded(true);
            toggle();
        }
    } catch (error) {
        return error;
    }
};

/**
 * @name getOneOffPaymentIframe Get the one off payment iframe
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} setHpp - Sets the hpp reponse from add card
 * @param {boolean} setLoading-sets the state of the loading iframe
 * @param {boolean} setOpenErrorPopup - Sets the error state when API is throwing error
 * @returns {string} Promise and resolves to add card response
 */
export const getOneOffPaymentIframe = async ({
    graphql,
    setHpp,
    setLoading,
    payableAmount,
    setOpenErrorPopup,
}) => {
    try {
        const response = await getStancerOneOffSelfcareHpp({
            graphql,
            payableAmount,
            setOpenErrorPopup,
            setLoading,
        });
        if (response) {
            setHpp(response?.getStancerOneOffSelfCareHpp);
            setLoading(false);
        }
    } catch (error) {
        return error;
    }
};

/**
 * @name validateOneOffpayment Validate the one off card details
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} paymentId - PaymentIntentId from add card response
 * @param {string} payableAmount - Specifies amount to be paid
 * @param {boolean} setStatus - Specifies state of payment status
 * @param {string} setPaidAmount - Sets the amount paid
 * @param {Function} handleNext - Function todisplay payment successful modal
 * @param {boolean} checked - Specifies state of payment status
 * @param {boolean} setPaymentErrorPopup -sets the state of payment error
 * @param {boolean} setOpenErrorPopup - Sets the error state when API is throwing error
 * @returns {string} Promise and resolves to one off payment response
 */
export const validateOneOffpayment = async ({
    graphql,
    paymentId,
    payableAmount,
    setStatus,
    setPaidAmount,
    handleNext,
    checked,
    setPaymentErrorPopup,
    setOpenErrorPopup,
}) => {
    try {
        const response = await validateStancerOneOffSelfCareHpp({
            graphql,
            paymentId,
            checked,
            setPaymentErrorPopup,
            setOpenErrorPopup,
        });
        if (response?.validateStancerOneOffSelfCareHpp === 'success') {
            setStatus(true);
            setPaidAmount(payableAmount);
            publish('UPDATE_BALANCE');
            handleNext();
        }
    } catch (error) {
        return error;
    }
};

/**
 * @function isCardValid - Filters the expired cards
 * @param {string} expiry
 * @returns {array} - Valid cards
 */
export const isCardValid = expiry => {
    const [expMonth, expYear] = expiry && expiry.split('/').map(Number);
    const today = new Date();
    const currentYear = today.getFullYear() % 100; // Last two digits of the year
    const currentMonth = today.getMonth() + 1; // Months are 0-based
    return expYear > currentYear || (expYear === currentYear && expMonth >= currentMonth);
};

/**
 * @function getCardIcon - Function to get svg card Icon based on credit card type
 * @param {string} type - Type of the credit card(Eg: VISA,MasterCard)
 * @returns {import('@emotion/react/types/jsx-namespace').ReactJSXElement} SVG icons for credit card type
 */
export const getCardIcon = type => {
    if (!type) return CREDIT_CARD;
    switch (type?.toLowerCase()) {
        case 'visa':
            return <VISAIcon />;
        case 'mastercard':
            return <MasterCardIcon />;
        default:
            return type;
    }
};

/**
 * @name structureSavedCardInfo To get the saved card info from reference
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} localStore - Local storage object
 * @returns {string} Promise and resolves to the saved card details
 */
const structureSavedCardInfo = paymentMethods =>
    paymentMethods?.map((card, index) => ({
        id: index + 1,
        cardId: card?.providerReference,
        cardType: card?.creditCardType || card?.type,
        cardExpiry: moment(card?.expirationDate).format('MM/YY'),
        paymentMethodType: card?.paymentMethodType,
        lastFourDigits: card?.partialDigits.slice(-4),
    }));

/**
 * @name getSavedCardDetails To get the saved card info from reference
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} localStore - Local storage object
 * @param {array} setSavedCards - sets the saved card in the form of array
 * @param {boolean} setLoading - sets the loading state of data
 * @returns {string} Promise and resolves to the saved card details
 */
export const getSavedCardDetails = async ({ graphql, setSavedCards, setLoading }) => {
    try {
        const response = await getPaymentMethods({
            graphql,
            variables: {
                token: sessionStorage.getItem('access_token'),
                account_id: sessionStorage.getItem('LoggedinUserAccountID'),
            },
        });
        const paymentMethods = response?.data?.getPaymentMethod?.paymentMethods?.filter(
            method => method?.paymentMethodType === CREDIT_CARD
        );
        const structuredCards = structureSavedCardInfo(paymentMethods);
        setSavedCards(structuredCards);
        setLoading(false);
        return response;
    } catch (error) {
        console.error('error', error);
        return error;
    }
};

/**
 * @name validateStancerOneOffSelfCareExistingCard Validate the one off card details
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} paymentId - paymentIntentId from getStancerSelfcareAddcardHpp response
 * @returns {string} Promise and resolves to add card response
 */
export const validateStancerOneOffSelfCareExistingCard = async ({
    graphql,
    payableAmount,
    cardId,
}) => {
    try {
        const data = await validateStancerOneOffHppExistingCard({
            graphql,
            variables: {
                token: sessionStorage.getItem('access_token'),
                cardId,
                paymentRequest: { amount: Math.round(payableAmount * 100) },
            },
        });
        return data;
    } catch (error) {
        return error;
    }
};

/**
 * @name validateOneOffpaymentExistingCard Validate the one off card details with existing card
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {object} paymentId - PaymentIntentId from add card response
 * @param {string} payableAmount - Specifies amount to be paid
 * @param {boolean} setStatus - Specifies state of payment status
 * @param {string} setPaidAmount - Sets the amount paid
 * @param {Function} handleNext - Function todisplay payment successful modal
 * @param {boolean} setLoading-sets the state of the loading iframe
 * @param {boolean} setOpenErrorPopup - Sets the error state when API is throwing error
 * @param {boolean} checked - Specifies state of payment status

 * @returns {string} Promise and resolves to one off payment response
 */
export const validateOneOffpaymentExistingCard = async ({
    graphql,
    payableAmount,
    setStatus,
    setPaidAmount,
    handleNext,
    cardId,
    setHpp,
    setLoadCvc,
    setStep,
    setLoading,
    setOpenErrorPopup,
}) => {
    try {
        const { result, errors } = await validateStancerOneOffSelfCareExistingCard({
            graphql,
            cardId,
            payableAmount,
        });
        if (result) {
            setStatus(true);
            setPaidAmount(payableAmount);
            publish('UPDATE_BALANCE');
            handleNext();
        } else if (errors) {
            const cvcInfo = errors?.error?.graphQLErrors[0]?.extensions?.additionalParameters;
            if (cvcInfo) {
                setHpp(cvcInfo);
                setLoadCvc(true);
                setLoading(false);
                setStep(1);
            } else {
                setOpenErrorPopup(true);
            }
        }
    } catch (error) {
        return error;
    }
};
